import { Parchment } from 'quill/core';
import React from 'react';
import type { AttachmentFile } from './Attachment';
import Comment from './Comment';
import Diff from './Diff';
import * as styles from './FileBlot.module.scss';
import ReactBlockEmbed from './ReactBlockEmbed';
import Reaction from './Reaction';
import VersionChange from './VersionChange';
import AttachmentUploader, {
  isAttachmentUploaded,
} from '../../utils/AttachmentUploader';
import { getAssetTokenFromBlot } from '../AssetTokenContext';
import FileComponent from '../file/File';
import { convertAttachmentToHtmlLink } from './utils/attachmentsHtml';

export type FileValue = {
  name: string;
  size: number;
  source: string;
};

class FileBlot extends ReactBlockEmbed<FileValue> {
  static blotName = 'file';
  static className = 'ql-file';
  static tagName = 'FIGURE';
  static supportedExtensions = new Set(['pdf']);

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: HTMLElement,
    value: FileValue,
  ) {
    super(scroll, domNode, { ...value });

    this.setState(value);
    this.domNode.classList.add(styles.container);
  }

  attach() {
    super.attach();
    AttachmentUploader.addActiveAttachmentBlot(this);
  }

  detach() {
    super.detach();
    AttachmentUploader.removeActiveAttachmentBlot(this);
  }

  format(name: string, value: File[]) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return { ...this.attributes?.values() };
  }

  html() {
    const attachmentFile = this.getAsAttachmentFile();
    if (!isAttachmentUploaded(attachmentFile)) {
      return '';
    }
    const attachmentLink = convertAttachmentToHtmlLink(
      attachmentFile,
      getAssetTokenFromBlot(this),
    );
    return `<div class="${FileBlot.className}">${attachmentLink}</div>`;
  }

  getAsAttachmentFile(): AttachmentFile {
    return this.store.getState();
  }

  isUploading() {
    const file = this.getAsAttachmentFile();
    return !isAttachmentUploaded(file);
  }

  cleanUpload(uploadFileId: string, source: string | null | undefined) {
    const currentValue = FileBlot.value(this.domNode) as FileValue;
    if (currentValue.source !== uploadFileId) {
      return;
    }

    if (source) {
      const newValue = { ...currentValue, source };
      this.setValue(newValue);
      this.setState(newValue);
    } else {
      this.remove();
    }
  }

  render(state: FileValue) {
    const file = this.getAsAttachmentFile();
    const isUploading = !isAttachmentUploaded(file);

    const isAnotherUserUploading =
      isUploading && !AttachmentUploader.shouldShowUploadingAttachment(file);

    if (isAnotherUserUploading) {
      return null;
    }

    return <FileComponent {...state} uploading={isUploading} />;
  }
}

export default FileBlot;
